import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token and organization context to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Add organization context header
    const organizationId = localStorage.getItem('current_organization_id');
    if (organizationId) {
      config.headers['X-Organization-ID'] = organizationId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 responses
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('admin_user');
      // Optionally redirect to login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password) => {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);
    const response = await axios.post(`${API_BASE_URL}/auth/login`, formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
    return response.data;
  },
  loginJson: (credentials) => axiosInstance.post('/auth/login/json', credentials).then(res => res.data),
  register: (data) => axiosInstance.post('/auth/register', data).then(res => res.data),
  getCurrentAdmin: () => axiosInstance.get('/auth/me').then(res => res.data),
  verifyToken: () => axiosInstance.post('/auth/verify').then(res => res.data),
  getMyOrganizations: () => axiosInstance.get('/auth/organizations').then(res => res.data),
};

// Organizations API (Super Admin only)
export const organizationsAPI = {
  getOrganizations: (params = {}) => axiosInstance.get('/organizations/', { params }).then(res => res.data),
  getOrganization: (id) => axiosInstance.get(`/organizations/${id}`).then(res => res.data),
  createOrganization: (data) => axiosInstance.post('/organizations/', data).then(res => res.data),
  updateOrganization: (id, data) => axiosInstance.put(`/organizations/${id}`, data).then(res => res.data),
  deleteOrganization: (id) => axiosInstance.delete(`/organizations/${id}`).then(res => res.data),
  getOrganizationUsers: (id) => axiosInstance.get(`/organizations/${id}/users`).then(res => res.data),
};

// Users API (Super Admin only)
export const usersAPI = {
  getUsers: (params = {}) => axiosInstance.get('/users/', { params }).then(res => res.data),
  getUser: (id) => axiosInstance.get(`/users/${id}`).then(res => res.data),
  createUser: (data) => axiosInstance.post('/users/', data).then(res => res.data),
  updateUser: (id, data) => axiosInstance.put(`/users/${id}`, data).then(res => res.data),
  deleteUser: (id) => axiosInstance.delete(`/users/${id}`).then(res => res.data),
  addUserToOrganization: (userId, data) =>
    axiosInstance.post(`/users/${userId}/organizations`, data).then(res => res.data),
  removeUserFromOrganization: (userId, organizationId) =>
    axiosInstance.delete(`/users/${userId}/organizations/${organizationId}`).then(res => res.data),
  updateUserRole: (userId, organizationId, role) =>
    axiosInstance.put(`/users/${userId}/organizations/${organizationId}/role`, null, { params: { role } }).then(res => res.data),
};

// Teams API
export const teamsAPI = {
  getTeams: (params = {}) => axiosInstance.get('/teams/', { params }).then(res => res.data),
  getTeam: (id) => axiosInstance.get(`/teams/${id}`).then(res => res.data),
  createTeam: (data) => axiosInstance.post('/teams/', data).then(res => res.data),
  updateTeam: (id, data) => axiosInstance.put(`/teams/${id}`, data).then(res => res.data),
  deleteTeam: (id) => axiosInstance.delete(`/teams/${id}`).then(res => res.data),
  addPlayerToTeam: (teamId, playerId) => axiosInstance.post(`/teams/${teamId}/players/${playerId}`).then(res => res.data),
  removePlayerFromTeam: (teamId, playerId) => axiosInstance.delete(`/teams/${teamId}/players/${playerId}`).then(res => res.data),
};

// Players API
export const playersAPI = {
  getPlayers: (params = {}) => axiosInstance.get('/players/', { params }).then(res => res.data),
  getPlayer: (id) => axiosInstance.get(`/players/${id}`).then(res => res.data),
  createPlayer: (data) => axiosInstance.post('/players/', data).then(res => res.data),
  updatePlayer: (id, data) => axiosInstance.put(`/players/${id}`, data).then(res => res.data),
  deletePlayer: (id) => axiosInstance.delete(`/players/${id}`).then(res => res.data),
  getTeamPlayers: (teamId) => axiosInstance.get(`/players/team/${teamId}`).then(res => res.data),
  updatePlayerStatistics: (id, statistics) => axiosInstance.put(`/players/${id}/statistics`, statistics).then(res => res.data),
  getPlayerStats: (id) => axiosInstance.get(`/players/${id}/stats`).then(res => res.data),
};

// Tournaments API
export const tournamentsAPI = {
  getTournaments: (params = {}) => axiosInstance.get('/tournaments/', { params }).then(res => res.data),
  getTournament: (id) => axiosInstance.get(`/tournaments/${id}`).then(res => res.data),
  createTournament: (data) => axiosInstance.post('/tournaments/', data).then(res => res.data),
  updateTournament: (id, data) => axiosInstance.put(`/tournaments/${id}`, data).then(res => res.data),
  deleteTournament: (id) => axiosInstance.delete(`/tournaments/${id}`).then(res => res.data),
  addTeamToTournament: (tournamentId, teamId) => axiosInstance.post(`/tournaments/${tournamentId}/teams/${teamId}`).then(res => res.data),
  removeTeamFromTournament: (tournamentId, teamId) => axiosInstance.delete(`/tournaments/${tournamentId}/teams/${teamId}`).then(res => res.data),
  updateTournamentStatus: (id, status) => axiosInstance.put(`/tournaments/${id}/status`, { status }).then(res => res.data),
};

// Matches API
export const matchesAPI = {
  getMatches: (params = {}) => axiosInstance.get('/matches/', { params }).then(res => res.data),
  getMatch: (id) => axiosInstance.get(`/matches/${id}`).then(res => res.data),
  createMatch: (data) => axiosInstance.post('/matches/', data).then(res => res.data),
  updateMatch: (id, data) => axiosInstance.put(`/matches/${id}`, data).then(res => res.data),
  deleteMatch: (id) => axiosInstance.delete(`/matches/${id}`).then(res => res.data),
  getTournamentMatches: (tournamentId) => axiosInstance.get(`/matches/tournament/${tournamentId}`).then(res => res.data),
  updateMatchStatus: (id, status) => axiosInstance.put(`/matches/${id}/status`, { status }).then(res => res.data),
  updateToss: (id, tossWinner, tossDecision) => axiosInstance.put(`/matches/${id}/toss`, { toss_winner: tossWinner, toss_decision: tossDecision }).then(res => res.data),
};

// Scorecards API
export const scorecardsAPI = {
  getScorecards: (params = {}) => axiosInstance.get('/scorecards/', { params }).then(res => res.data),
  getScorecard: (id) => axiosInstance.get(`/scorecards/${id}`).then(res => res.data),
  createScorecard: (data) => axiosInstance.post('/scorecards/', data).then(res => res.data),
  updateScorecard: (id, data) => axiosInstance.put(`/scorecards/${id}`, data).then(res => res.data),
  deleteScorecard: (id) => axiosInstance.delete(`/scorecards/${id}`).then(res => res.data),
  getMatchScorecard: (matchId) => axiosInstance.get(`/scorecards/match/${matchId}`).then(res => res.data),
  updateBattingScorecard: (id, teamId, battingData) => axiosInstance.put(`/scorecards/${id}/batting`, { team_id: teamId, ...battingData }).then(res => res.data),
  updateBowlingScorecard: (id, teamId, bowlingData) => axiosInstance.put(`/scorecards/${id}/bowling`, { team_id: teamId, ...bowlingData }).then(res => res.data),
  
  // New step-by-step scorecard APIs
  initializeScorecard: (data) => axiosInstance.post('/scorecards/init', data).then(res => res.data),
  addBallToScorecard: (scorecardId, ballData) => axiosInstance.post(`/scorecards/${scorecardId}/ball`, ballData).then(res => res.data),
  liveScoreUpdate: (scorecardId, liveUpdateData) => axiosInstance.post(`/scorecards/${scorecardId}/live-update`, liveUpdateData).then(res => res.data),
  completeScorecard: (scorecardId, manOfTheMatch = null) => axiosInstance.post(`/scorecards/${scorecardId}/complete`, { man_of_the_match: manOfTheMatch }).then(res => res.data),
  
  // Player APIs for match
  getTeamPlayers: (teamId) => axiosInstance.get(`/players/team/${teamId}`).then(res => res.data),
  getMatchPlayers: (matchId) => axiosInstance.get(`/players/match/${matchId}`).then(res => res.data),
  
  // Scorecard management APIs
  resetInnings: (scorecardId, inningsNumber) => axiosInstance.post(`/scorecards/${scorecardId}/reset-innings`, { innings_number: inningsNumber }).then(res => res.data),
  undoBall: (scorecardId, currentInnings, matchId) => axiosInstance.post(`/scorecards/${scorecardId}/undo-ball`, { current_innings: currentInnings, match_id: matchId }).then(res => res.data),

  // Super Over APIs
  startSuperOver: (scorecardId) => axiosInstance.post(`/scorecards/${scorecardId}/super-over/start`).then(res => res.data),
  addSuperOverBall: (scorecardId, team, ballData) => axiosInstance.post(`/scorecards/${scorecardId}/super-over/ball?team=${team}`, ballData).then(res => res.data),

  // Bowler limits API
  getBowlerLimits: (scorecardId, innings = 1) => axiosInstance.get(`/scorecards/${scorecardId}/bowler-limits?innings=${innings}`).then(res => res.data),
};

// Points Table API
export const pointsTableAPI = {
  initializePointsTable: (tournamentId) => axiosInstance.post(`/points-table/tournament/${tournamentId}/initialize`).then(res => res.data),
  getPointsTable: (tournamentId) => axiosInstance.get(`/points-table/tournament/${tournamentId}`).then(res => res.data),
  updatePointsTable: (tournamentId) => axiosInstance.post(`/points-table/tournament/${tournamentId}/update`).then(res => res.data),
  updateStandingsAfterMatch: (matchId) => axiosInstance.post(`/points-table/match/${matchId}/update-standings`).then(res => res.data),
  getGroupStandings: (tournamentId, groupId) => axiosInstance.get(`/points-table/tournament/${tournamentId}/group/${groupId}`).then(res => res.data),
};

// Venues API
export const venuesAPI = {
  getVenues: (params = {}) => axiosInstance.get('/venues/', { params }).then(res => res.data),
  getVenue: (id) => axiosInstance.get(`/venues/${id}`).then(res => res.data),
  createVenue: (data) => axiosInstance.post('/venues/', data).then(res => res.data),
  updateVenue: (id, data) => axiosInstance.put(`/venues/${id}`, data).then(res => res.data),
  deleteVenue: (id) => axiosInstance.delete(`/venues/${id}`).then(res => res.data),
  incrementMatchesPlayed: (id) => axiosInstance.post(`/venues/${id}/increment-matches`).then(res => res.data),
  updateVenueStats: (id, firstInningsScore, secondInningsScore) =>
    axiosInstance.post(`/venues/${id}/update-stats`, { first_innings_score: firstInningsScore, second_innings_score: secondInningsScore }).then(res => res.data),
  searchByLocation: (lat, lng, radiusKm = 50) =>
    axiosInstance.get('/venues/search/by-location', { params: { lat, lng, radius_km: radiusKm } }).then(res => res.data),
};

// Umpires API
export const umpiresAPI = {
  getUmpires: (params = {}) => axiosInstance.get('/umpires/', { params }).then(res => res.data),
  getUmpire: (id) => axiosInstance.get(`/umpires/${id}`).then(res => res.data),
  createUmpire: (data) => axiosInstance.post('/umpires/', data).then(res => res.data),
  updateUmpire: (id, data) => axiosInstance.put(`/umpires/${id}`, data).then(res => res.data),
  deleteUmpire: (id) => axiosInstance.delete(`/umpires/${id}`).then(res => res.data),
  getAvailableUmpires: (matchDate, umpireType = null, level = null) =>
    axiosInstance.get('/umpires/available/for-date', { params: { match_date: matchDate, umpire_type: umpireType, level } }).then(res => res.data),
  addUnavailableDate: (id, date) => axiosInstance.post(`/umpires/${id}/add-unavailable-date`, null, { params: { unavailable_date: date } }).then(res => res.data),
  removeUnavailableDate: (id, date) => axiosInstance.post(`/umpires/${id}/remove-unavailable-date`, null, { params: { unavailable_date: date } }).then(res => res.data),
  incrementMatchesOfficiated: (id, isInternational = false) => axiosInstance.post(`/umpires/${id}/increment-matches`, null, { params: { is_international: isInternational } }).then(res => res.data),
  assignToMatch: (assignment) => axiosInstance.post(`/umpires/match/${assignment.match_id}/assign`, assignment).then(res => res.data),
  getMatchUmpires: (matchId) => axiosInstance.get(`/umpires/match/${matchId}/assignment`).then(res => res.data),
};

// Squads API
export const squadsAPI = {
  // Tournament Squad
  createTournamentSquad: (data) => axiosInstance.post('/squads/tournament', data).then(res => res.data),
  getTournamentSquad: (tournamentId, teamId) => axiosInstance.get(`/squads/tournament/${tournamentId}/team/${teamId}`).then(res => res.data),
  updateTournamentSquad: (tournamentId, teamId, data) => axiosInstance.put(`/squads/tournament/${tournamentId}/team/${teamId}`, data).then(res => res.data),
  confirmTournamentSquad: (tournamentId, teamId) => axiosInstance.post(`/squads/tournament/${tournamentId}/team/${teamId}/confirm`).then(res => res.data),

  // Match Squad
  createMatchSquad: (data) => axiosInstance.post('/squads/match', data).then(res => res.data),
  getMatchSquad: (matchId, teamId) => axiosInstance.get(`/squads/match/${matchId}/team/${teamId}`).then(res => res.data),
  getBothMatchSquads: (matchId) => axiosInstance.get(`/squads/match/${matchId}`).then(res => res.data),
  updateMatchSquad: (matchId, teamId, data) => axiosInstance.put(`/squads/match/${matchId}/team/${teamId}`, data).then(res => res.data),
  setPlayingXI: (matchId, teamId, playingXIRequest) => axiosInstance.post(`/squads/match/${matchId}/team/${teamId}/playing-xi`, playingXIRequest).then(res => res.data),
  announcePlayingXI: (matchId, teamId) => axiosInstance.post(`/squads/match/${matchId}/team/${teamId}/announce`).then(res => res.data),
  setImpactPlayer: (matchId, teamId, impactPlayerId, replacingPlayerId) =>
    axiosInstance.post(`/squads/match/${matchId}/team/${teamId}/impact-player`, null, { params: { impact_player_id: impactPlayerId, replacing_player_id: replacingPlayerId } }).then(res => res.data),
  createFromTournamentSquad: (matchId, teamId) => axiosInstance.post(`/squads/match/${matchId}/team/${teamId}/from-tournament`).then(res => res.data),
};

// Live Score API (Public)
export const liveScoreAPI = {
  getLiveMatches: () => axiosInstance.get('/live/matches/live').then(res => res.data),
  getLiveMatchDetail: (matchId) => axiosInstance.get(`/live/match/${matchId}`).then(res => res.data),
  getTodaysMatches: () => axiosInstance.get('/live/matches/today').then(res => res.data),
  getUpcomingMatches: (days = 7, tournamentId = null) =>
    axiosInstance.get('/live/matches/upcoming', { params: { days, tournament_id: tournamentId } }).then(res => res.data),
  getRecentMatches: (days = 7, tournamentId = null) =>
    axiosInstance.get('/live/matches/recent', { params: { days, tournament_id: tournamentId } }).then(res => res.data),
  getTournamentSummary: (tournamentId) => axiosInstance.get(`/live/tournament/${tournamentId}/summary`).then(res => res.data),
};

// Reports API
export const reportsAPI = {
  getTournamentReport: (tournamentId) => axiosInstance.get(`/reports/tournament/${tournamentId}/summary`).then(res => res.data),
  getTopScorers: (tournamentId, limit = 10) => axiosInstance.get(`/reports/tournament/${tournamentId}/top-scorers`, { params: { limit } }).then(res => res.data),
  getTopWicketTakers: (tournamentId, limit = 10) => axiosInstance.get(`/reports/tournament/${tournamentId}/top-wicket-takers`, { params: { limit } }).then(res => res.data),
  getMatchScorecardReport: (matchId) => axiosInstance.get(`/reports/match/${matchId}/scorecard`).then(res => res.data),
  getPlayerCareerReport: (playerId) => axiosInstance.get(`/reports/player/${playerId}/career`).then(res => res.data),

  // Export endpoints
  exportPointsTableCSV: (tournamentId) => window.open(`${API_BASE_URL}/reports/tournament/${tournamentId}/export/points-table`, '_blank'),
  exportFixturesCSV: (tournamentId) => window.open(`${API_BASE_URL}/reports/tournament/${tournamentId}/export/fixtures`, '_blank'),
  exportTopPerformersCSV: (tournamentId) => window.open(`${API_BASE_URL}/reports/tournament/${tournamentId}/export/top-performers`, '_blank'),
  exportScorecardJSON: (matchId) => window.open(`${API_BASE_URL}/reports/match/${matchId}/export/scorecard?format=json`, '_blank'),
  exportScorecardCSV: (matchId) => window.open(`${API_BASE_URL}/reports/match/${matchId}/export/scorecard?format=csv`, '_blank'),
};

// Tournaments API Extensions
export const tournamentsExtendedAPI = {
  ...tournamentsAPI,
  // Fixture Generation
  generateFixtures: (tournamentId, params = {}) => axiosInstance.post(`/tournaments/${tournamentId}/generate-fixtures`, null, { params }).then(res => res.data),

  // Bracket Management
  getBracket: (tournamentId) => axiosInstance.get(`/tournaments/${tournamentId}/bracket`).then(res => res.data),
  updateBracketMatch: (tournamentId, bracketMatchId, winnerId) =>
    axiosInstance.post(`/tournaments/${tournamentId}/bracket/update-match`, null, { params: { bracket_match_id: bracketMatchId, winner_id: winnerId } }).then(res => res.data),

  // Group Management
  configureGroups: (tournamentId, numGroups = 2, teamsPerGroup = null, randomDraw = true) =>
    axiosInstance.post(`/tournaments/${tournamentId}/groups/configure`, null, { params: { num_groups: numGroups, teams_per_group: teamsPerGroup, random_draw: randomDraw } }).then(res => res.data),
  addTeamToGroup: (tournamentId, groupId, teamId) =>
    axiosInstance.post(`/tournaments/${tournamentId}/groups/${groupId}/add-team`, null, { params: { team_id: teamId } }).then(res => res.data),
  getGroups: (tournamentId) => axiosInstance.get(`/tournaments/${tournamentId}/groups`).then(res => res.data),
  generateKnockoutFromGroups: (tournamentId) => axiosInstance.post(`/tournaments/${tournamentId}/generate-knockout-from-groups`).then(res => res.data),
};

// Import/Export API
export const importExportAPI = {
  exportAll: () => axiosInstance.get('/data/export/all').then(res => res.data),
  importAll: (data) => axiosInstance.post('/data/import/all', data).then(res => res.data),
};

// Combined API object for easier imports
export const api = {
  ...authAPI,
  ...organizationsAPI,
  ...usersAPI,
  ...teamsAPI,
  ...playersAPI,
  ...tournamentsAPI,
  ...matchesAPI,
  ...scorecardsAPI,
  ...pointsTableAPI,
  ...venuesAPI,
  ...umpiresAPI,
  ...squadsAPI,
  ...liveScoreAPI,
  ...reportsAPI,
  ...tournamentsExtendedAPI,
};

export default api;
